import React from 'react';
import { Button, Chip } from '@mui/material';
import { DataManagementConfig } from '@shared/components/data-management/types';
import { SurveyAuditTableData } from '../types';
import { createArchivedAuditsDataManagementHooks } from '../hooks/useArchivedAuditsForDataManagement';
import { notificationService } from '@shared/components/notifications/notificationService';

/**
 * Creates table configuration for audits with ARCHIVED status
 * These are audits that have been archived and hidden from the active view
 *
 * @param subjectId - Subject ID for scoping audits
 * @param tenantId - Tenant ID for context
 * @param domainId - Domain ID for context
 * @param navigate - Optional navigation function
 */
export const useArchivedAuditsConfig = (
  subjectId: string,
  tenantId: string,
  domainId: string,
  _navigate?: (path: string) => void
): DataManagementConfig<SurveyAuditTableData> => {
  // Get data management hooks
  const { useArchivedAuditsQuery, useUnarchiveAuditWrapper } =
    createArchivedAuditsDataManagementHooks(subjectId, tenantId, domainId);

  const unarchiveMutation = useUnarchiveAuditWrapper();

  return {
    // Data hooks
    hooks: {
      useQuery: useArchivedAuditsQuery,
    },

    // Table configuration
    table: {
      title: 'Archived Audits',
      columns: [
        // Survey column
        {
          label: 'Survey',
          key: 'surveyTitle',
        },

        // Created date column
        {
          label: 'Created',
          key: 'createdAt',
          render: (value) => {
            if (!value) return '-';
            if (typeof value === 'string' || typeof value === 'number') {
              return new Date(value).toLocaleDateString();
            }
            return '-';
          },
        },

        // Response count column
        {
          label: 'Responses',
          key: 'responseCount',
          render: (value) => value?.toString() || '0',
        },

        // Status chip column
        {
          label: 'Status',
          key: 'status',
          render: () => <Chip label="Archived" color="default" variant="outlined" size="small" />,
        },

        // Actions column with Unarchive button
        {
          label: 'Actions',
          key: 'id',
          render: (value, row) => (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await unarchiveMutation.mutateAsync({
                    auditId: row.id,
                    subjectId: row.subjectId,
                  });
                  notificationService.success(`"${row.surveyTitle}" has been unarchived`);
                } catch (error) {
                  console.error('Failed to unarchive audit:', error);
                  notificationService.error('Failed to unarchive audit. Please try again.');
                }
              }}
            >
              Unarchive
            </Button>
          ),
        },
      ],
      emptyMessage: '📦 No archived audits!\nArchived audits will appear here.',
      errorMessage: 'Failed to load archived audits. Please try again.',
    },

    // Table features
    features: {
      search: true,
      pagination: true,
    },
  };
};
